import styled from 'styled-components';
import { useEffect, useState } from 'react';

import { DESKTOP_PADDING, MOBILE_PADDING } from 'homepage/styles/constants';
import { fetchBrandListSection, fetchSearchMetaData } from 'homepage/services/homepageApi';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { setCurrentFeedData } from 'store/actions/homepageActions';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useAbortController } from 'shared/hooks/useAbortController';
import { useStoreDispatch } from 'store/hooks/storeHooks';
import { variableFontRegular } from 'styles/theme/text';
import { CuratedLinkTableCarousel } from 'homepage/components/brandsListSection/CuratedLinkTableCarousel';

import { IConstructorUserParams } from 'search/types/ConstructorUserParams';
import { IModuleTrackingData } from 'homepage/types/ModuleTrackingData';
import { IFeedSection, ISubSection } from 'homepage/types/Feed';
import { t } from 'localization/utils/t';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';
import { getAllfilters } from 'shared/components/product-grid/utils/gridUtils';
import { capitalize } from 'lodash/fp';
import { getAllBrandsFromConstructor } from './utils';

const TP = 'homepage.components.sections.BrandListSection';

interface IBrandListSection {
  constructorUserParams?: IConstructorUserParams;
  requestMade?: boolean;
  section: IFeedSection;
  sectionIndex: number;
  subsections: ISubSection[];
  moduleTrackingData: IModuleTrackingData;
}

export const BrandListSection = ({
  constructorUserParams,
  moduleTrackingData,
  requestMade = false,
  section,
  sectionIndex,
  subsections,
}: IBrandListSection) => {
  const isMounted = useIsMounted();
  const storeDispatch = useStoreDispatch();

  const { signal } = useAbortController()();

  const [brands, setBrands] = useState(subsections || []);
  const isNewSearchPageEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  useEffect(() => {
    const getConsumerSearchBrands = async () => {
      const finalBrands = [];
      try {
        let response: any = {};
        try {
          response = await fetchSearchMetaData();
        } catch {
          response = {};
        }
        const allBrandsFromConstructor = getAllBrandsFromConstructor(response || {});

        const csBrands = [];

        if (allBrandsFromConstructor) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          Object.keys(allBrandsFromConstructor || {}).forEach((b) => {
            csBrands.push({
              ...(allBrandsFromConstructor?.[b] || {}),
              title: b,
              count: allBrandsFromConstructor?.[b]?.count,
            });
          });
        }

        // console.log('allBrandsFromConstructor', allBrandsFromConstructor);

        // for each brands call up on the search api to get aggregations
        const brandsCounts = await getAllfilters({
          brands: csBrands.map((brand) =>
            brand.title === 'Adidas' ? brand.title.toLowerCase() : brand.title || '',
          ),
          preFilters: { brands: true },
        });

        const brandsAgg = brandsCounts.find((bc) => bc?.bucketName === 'brands')?.valuesList;

        csBrands.forEach((brand) => {
          const brandeuh = brandsAgg?.find((b) =>
            ['adidas'].includes(b.value)
              ? brand.title === 'Adidas'
              : b?.value === brand.title || b?.value === brand.slug,
          );
          if (brandeuh && brandeuh?.count > 0) {
            finalBrands.push({
              ...brand,
              title: capitalize(brandeuh.value || ''),
              count: brandeuh.count,
              searchConfig: {
                filterValue: brandeuh.slug,
              },
            });
          }
        });

        if (isMounted()) {
          setBrands(finalBrands || []);
        }
      } catch (error) {
        bugsnag(error, { context: TP });
        setBrands(finalBrands || []);
      }
    };

    if (isNewSearchPageEnabled) {
      void getConsumerSearchBrands();
    }
  }, [isNewSearchPageEnabled]);

  useEffect(() => {
    const getBrands = async (abortSignal) => {
      const response = await fetchBrandListSection(
        section,
        subsections,
        constructorUserParams,
        abortSignal,
      );
      if (isMounted()) {
        setBrands(response?.subsections);
      }
    };

    if (!requestMade && constructorUserParams && !isNewSearchPageEnabled) {
      void getBrands(signal);
    }
  }, [constructorUserParams, isNewSearchPageEnabled]);

  useEffect(() => {
    if (brands && isMounted()) {
      storeDispatch(setCurrentFeedData({ index: sectionIndex, subsections: brands }));
    }
  }, [brands]);

  return (
    <BrandListSection.Container>
      <BrandListSection.Header>
        <BrandListSection.Title>{t(`${TP}.brands`, 'Brands')}</BrandListSection.Title>
      </BrandListSection.Header>
      <CuratedLinkTableCarousel curatedLinks={brands} moduleTrackingData={moduleTrackingData} />
    </BrandListSection.Container>
  );
};

BrandListSection.Container = styled.div`
  margin: 32px 0px;
  width: 100%;
  max-width: 100%;
  overflow-x: scroll;
  overflow-y: hidden;
  padding-left: ${MOBILE_PADDING};
  ${hideScrollbars}

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin: 60px 0px;
    padding-left: ${DESKTOP_PADDING};
  }
`;

BrandListSection.Header = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
  padding-right: ${DESKTOP_PADDING};
`;

BrandListSection.Title = styled.h2`
  font-weight: 400;
  text-transform: capitalize;
  ${variableFontRegular({ size: '13px', height: '15px' })};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    font-weight: 600;
    ${variableFontRegular({ size: '11.5px', height: '14px' })};
  }
`;
